import { Card, CardBody, Image, Spinner } from "@nextui-org/react";
import { IconCheck, IconX } from "@tabler/icons-react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AssociationUserShowProvider,
  useAssociationUserShow,
} from "../../context/user-show.context";
import { useFileEntityUrl } from "../../hooks";
import { getCountryFlagUrl } from "../../lib/flag-map";
import { AssociationMembership, AssociationUser } from "../../sdk";

/**
 * This page is reached by scanning a member's QR code, so the visitor is
 * normally not logged in. It must not use the shared `axiosInstance` from the
 * data provider: that instance has a request interceptor which throws
 * "No token found" when localStorage holds no admin token, so the request
 * would never leave the browser.
 */
const publicHttpClient = axios.create();

type FetchState =
  | { status: "loading" }
  | { status: "found"; associationUser: AssociationUser }
  | { status: "notFound" }
  | { status: "error" };

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className={
      "bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))]  from-primary-shade via-primary to-primary-shade h-screen mx-auto"
    }
  >
    {children}
  </div>
);

const CenteredMessage: React.FC<{ title: string; detail: string }> = ({
  title,
  detail,
}) => (
  <div className={"flex justify-center items-center h-full p-8"}>
    <Card className={"bg-white/90 max-w-sm"}>
      <CardBody className={"text-center"}>
        <b>{title}</b>
        <p className={"text-slate-500"}>{detail}</p>
      </CardBody>
    </Card>
  </div>
);

export const PublicUserView = () => {
  const { userId, associationUserId } = useParams();
  const [state, setState] = useState<FetchState>({ status: "loading" });

  useEffect(() => {
    if (!userId || !associationUserId) {
      setState({ status: "notFound" });
      return;
    }

    const abortController = new AbortController();

    publicHttpClient
      .get<AssociationUser | "">(
        `${
          import.meta.env.VITE_PUBLIC_BACKEND_API_URL
        }/public-profile/${userId}/${associationUserId}`,
        { signal: abortController.signal }
      )
      .then(({ data }) => {
        // The endpoint answers 200 with an empty body for unknown ids on
        // deployments that predate the 404 fix, so treat that as not found.
        if (!data) {
          setState({ status: "notFound" });
          return;
        }
        setState({ status: "found", associationUser: data });
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        setState({
          status: error.response?.status === 404 ? "notFound" : "error",
        });
      });

    return () => abortController.abort();
  }, [userId, associationUserId]);

  if (state.status === "loading") {
    return (
      <PageShell>
        <div className={"flex justify-center items-center h-full"}>
          <Spinner color={"white"} />
        </div>
      </PageShell>
    );
  }

  if (state.status === "notFound") {
    return (
      <PageShell>
        <CenteredMessage
          title={"Membership card not found"}
          detail={
            "This card is no longer valid. Please ask the member for an up to date card."
          }
        />
      </PageShell>
    );
  }

  if (state.status === "error") {
    return (
      <PageShell>
        <CenteredMessage
          title={"Something went wrong"}
          detail={"The membership card could not be loaded. Please try again."}
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <AssociationUserShowProvider associationUser={state.associationUser}>
        <UserView />
      </AssociationUserShowProvider>
    </PageShell>
  );
};

export const UserView = () => {
  const { associationUser } = useAssociationUserShow();
  return (
    <div className={"p-8 gap-y-2 flex flex-col"}>
      <div className="flex justify-center my-2">
        <Image
          alt="Woman listing to music"
          className="object-cover rounded-full border-4 border-secondary"
          height={250}
          src={useFileEntityUrl(associationUser?.user?.photoId)}
          width={250}
        />
      </div>

      <Card className={"bg-white/90"}>
        <CardBody>
          <p className={"text-slate-500"}>User name</p>
          <b>
            {associationUser?.user?.firstName} {associationUser?.user?.lastName}
          </b>
        </CardBody>
      </Card>

      <Card className={"bg-white/90"}>
        <CardBody>
          <p className={"text-slate-500"}>Role</p>
          <span>
            <b>{associationUser?.associationRole?.name}</b>
          </span>
        </CardBody>
      </Card>

      <Card className={"bg-white/90"}>
        <CardBody>
          <p className={"text-slate-500"}>Association</p>
          <div className={"flex flex-row"}>
            <span className={"flex"}>
              <b>{associationUser?.association?.name}</b>
            </span>
            <CountryFlag />
          </div>
        </CardBody>
      </Card>

      <Card
        className={`bg-white/100 border-4 ${
          isValidMembership(associationUser?.membership)
            ? "border-success"
            : "border-danger"
        }`}
      >
        <CardBody>
          <p className={"text-slate-500"}>Membership</p>
          <span className={"flex"}>
            <b className={"mr-1"}>
              {isValidMembership(associationUser?.membership)
                ? "Valid membership"
                : "Invalid membership"}
            </b>
            {isValidMembership(associationUser?.membership) ? (
              <IconCheck className={"text-success"} />
            ) : (
              <IconX className={"text-danger"} />
            )}
          </span>
        </CardBody>
      </Card>
    </div>
  );
};

const isValidMembership = (memberships?: AssociationMembership[]) => {
  if (!memberships) return false;
  const currentDate = Date.now();
  return memberships.some(
    (membership) =>
      new Date(membership["startDate"]).getTime() < currentDate &&
      new Date(membership["endDate"]).getTime() > currentDate
  );
};

const CountryFlag = () => {
  const { associationUser } = useAssociationUserShow();
  return (
    <div
      className={
        "w-10 h-10 text-large border-secondary rounded-full flex flex-col justify-center absolute top-5 right-5"
      }
    >
      <Image src={getCountryFlagUrl(associationUser?.association?.country)} />
    </div>
  );
};
